<template>
  <div class="px-4 py-4 mb-18">
    <div class="mb-4">
      <h2 class="text-sm font-semibold text-neutral-800 mb-1">Wann würdest du gerne ein Meeting vereinbaren?</h2>
    </div>
    
    <!-- Date Selection -->
    <div class="mb-4">
      <label class="block text-xs font-medium text-neutral-600 mb-1">Datum</label>
      <input 
        type="date" 
        v-model="localFormData.date"
        class="form-input"
        :style="{
          width: '100%',
          backgroundColor: '#f9fafb',
          border: '1px solid #e5e7eb',
          padding: '0.5rem 0.75rem',
          borderRadius: '0.375rem',
          fontSize: '0.75rem',
          color: '#374151',
          transition: 'all 0.2s ease'
        }"
        :min="minDate"
        @focus="handleInputFocus"
        @blur="handleInputBlur"
        @mouseover="handleInputHover"
        @mouseout="handleInputMouseOut"
      />
    </div>
    
    <!-- Time Grid -->
    <div class="mb-4">
      <label class="block text-xs font-medium text-neutral-600 mb-2">Uhrzeit</label>
      <div class="grid grid-cols-3 gap-2">
        <button 
          v-for="time in availableTimes" 
          :key="time"
          @click="selectTime(time)"
          class="time-button"
          :class="{ 'selected': localFormData.time === time }"
          :style="{
            padding: '0.5rem 0.75rem',
            borderRadius: '0.375rem',
            fontSize: '0.75rem',
            fontWeight: '500',
            transition: 'all 0.2s ease',
            border: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb',
            color: '#374151',
            cursor: 'pointer',
            ...getTimeButtonStyle(time)
          }"
          @mouseover="handleTimeButtonHover($event, time)"
          @mouseout="handleTimeButtonMouseOut($event, time)"
        >
          {{ time }}
        </button>
      </div>
    </div>

    <!-- Duration -->
    <div class="mb-6">
      <label class="block text-xs font-medium text-neutral-600 mb-1">Dauer</label>
      <select 
        v-model="localFormData.duration" 
        class="form-input"
        :style="{
          width: '100%',
          backgroundColor: '#f9fafb',
          border: '1px solid #e5e7eb',
          padding: '0.5rem 0.75rem',
          borderRadius: '0.375rem',
          fontSize: '0.75rem',
          color: '#374151',
          transition: 'all 0.2s ease'
        }"
        @focus="handleInputFocus"
        @blur="handleInputBlur"
        @mouseover="handleInputHover"
        @mouseout="handleInputMouseOut"
      >
        <option value="15">15 Minuten</option>
        <option value="30">30 Minuten</option>
        <option value="45">45 Minuten</option>
        <option value="60">1 Stunde</option>
      </select>
    </div>

    <!-- Continue Button -->
    <div class="flex justify-end">
      <button 
        @click="nextStep"
        class="continue-button"
        :style="{
          padding: '0.6rem 1rem',
          fontSize: '0.75rem',
          fontWeight: '500',
          borderRadius: '0.375rem',
          border: 'none',
          cursor: isValid ? 'pointer' : 'not-allowed',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          opacity: isValid ? '1' : '0.7',
          ...getContinueButtonStyle()
        }"
        :disabled="!isValid"
        @mouseover="handleContinueButtonHover"
        @mouseout="handleContinueButtonMouseOut"
      >
        Weiter
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MeetingStep1',
  props: {
    formData: {
      type: Object,
      required: true
    },
    availableTimes: {
      type: Array,
      required: true
    },
    minDate: {
      type: String,
      required: true
    },
    styles: {
      type: Object,
      default: () => ({
        bubbleColor: '#3b82f6',
        textColor: '#ffffff'
      })
    }
  },
  data() {
    return {
      localFormData: { ...this.formData }
    };
  },
  computed: {
    isValid() {
      console.log('Validation check:', {
        date: this.localFormData.date,
        time: this.localFormData.time,
        duration: this.localFormData.duration,
        isValid: !!(this.localFormData.date && this.localFormData.time && this.localFormData.duration)
      });
      
      return this.localFormData.date && 
             this.localFormData.time && 
             this.localFormData.duration;
    }
  },
  watch: {
    localFormData: {
      deep: true,
      handler(newValue) {
        console.log('Form data updated:', newValue);
        this.$emit('update:form-data', newValue);
      }
    }
  },
  mounted() {
    console.log('MeetingStep1 mounted with props:', {
      formData: this.formData,
      availableTimes: this.availableTimes,
      minDate: this.minDate,
      styles: this.styles
    });
    console.log('Initial localFormData:', this.localFormData);
  },
  methods: {
    // Handle input focus/hover for Shadow DOM
    handleInputFocus(event) {
      event.target.style.borderColor = '#d1d5db';
      event.target.style.backgroundColor = 'white';
      event.target.style.boxShadow = '0 0 0 1px #d1d5db';
    },
    
    handleInputBlur(event) {
      event.target.style.borderColor = '#e5e7eb';
      event.target.style.backgroundColor = '#f9fafb';
      event.target.style.boxShadow = 'none';
    },
    
    handleInputHover(event) {
      if (document.activeElement !== event.target) {
        event.target.style.borderColor = '#d1d5db';
        event.target.style.backgroundColor = 'white';
      }
    },
    
    handleInputMouseOut(event) {
      if (document.activeElement !== event.target) {
        event.target.style.borderColor = '#e5e7eb';
        event.target.style.backgroundColor = '#f9fafb';
      }
    },

    // Handle time button hover for Shadow DOM
    handleTimeButtonHover(event, time) {
      if (this.localFormData.time !== time) {
        event.target.style.backgroundColor = 'white';
        event.target.style.borderColor = '#d1d5db';
      }
    },
    
    handleTimeButtonMouseOut(event, time) {
      if (this.localFormData.time !== time) {
        event.target.style.backgroundColor = '#f9fafb';
        event.target.style.borderColor = '#e5e7eb';
      }
    },

    // Handle continue button hover for Shadow DOM
    handleContinueButtonHover(event) {
      if (this.isValid) {
        event.target.style.transform = 'translateY(-1px)';
        event.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
      }
    },
    
    handleContinueButtonMouseOut(event) {
      if (this.isValid) {
        event.target.style.transform = 'none';
        event.target.style.boxShadow = 'none';
      }
    },

    selectTime(time) {
      console.log('Time selected:', time);
      this.localFormData.time = time;
    },
    
    nextStep() {
      console.log('Next step clicked, isValid:', this.isValid);
      if (this.isValid) {
        console.log('Emitting next-step with data:', this.localFormData);
        this.$emit('next-step');
      }
    },
    
    // Get dynamic styling for time buttons
    getTimeButtonStyle(time) {
      const isSelected = this.localFormData.time === time;
      const bubbleColor = this.styles.bubbleColor || '#3b82f6';
      
      if (isSelected) {
        // Selected state: bubble color background with 10% opacity, full opacity border, and text color
        return {
          backgroundColor: this.hexToRgba(bubbleColor, 0.1),
          borderColor: bubbleColor,
          color: bubbleColor
        };
      } else {
        // Unselected state: default styling
        return {};
      }
    },
    
    // Get dynamic styling for continue button
    getContinueButtonStyle() {
      const bubbleColor = this.styles.bubbleColor || '#3b82f6';
      const textColor = this.styles.textColor || '#ffffff';
      
      return {
        backgroundColor: bubbleColor,
        color: textColor
      };
    },
    
    // Helper function to convert hex to rgba
    hexToRgba(hex, alpha) {
      // Remove # if present
      hex = hex.replace('#', '');
      
      // Parse hex values
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
  }
};
</script>

<style scoped>
/* Minimal styles - animations and transitions that can't be done inline */
</style>